/* /app/frontend/src/data/audiencePages.js
   ----------------------------------------
   Config-as-data for each audience landing page.  The <AudiencePage>
   component renders this verbatim - keep copy factual (no fabricated
   stats, testimonials, or claims) and route every CTA at a page or
   tool that already exists. */

const ALPHABET_RIGHT =
  "/images/dict/BSL-Fingerspelling-Right-Handed-1024x724.png";
const ALPHABET_LEFT =
  "/images/dict/BSL-Fingerspelling-Left-Handed-1024x724.png";
const ALPHABET_PDF_R =
  "/downloads/BSL-Fingerspelling-Right-Handed.pdf";
const ALPHABET_PDF_L =
  "/downloads/BSL-Fingerspelling-Left-Handed.pdf";

/* The five reusable tool cards.  Audience pages mix-and-match this
   set with a `useNote` overriding the generic blurb for that
   audience. */
const TOOLS = {
  vocabSheet: {
    id: "vocab-sheet",
    name: "Vocab Sheet Maker",
    href: "/bsl-vocabulary-sheet-creator",
    blurb:
      "Choose up to 12 signs from our library, pick a colour and download a printable A4 sheet.",
    visualKind: "preview-vocab",
  },
  wordSearch: {
    id: "word-search",
    name: "Word Search Maker",
    href: "/fingerspelling-word-search-maker",
    blurb:
      "Build a BSL fingerspelling word search around any theme or your own list of words.",
    visualKind: "preview-wordsearch",
  },
  fingerspell: {
    id: "fingerspell",
    name: "Fingerspelling Player",
    href: "/fingerspell",
    blurb:
      "Type any name or word and watch it fingerspelled in BSL, with adjustable speed, hand preference and skin tone.",
    visualKind: "preview-fingerspell",
  },
  fingerspellPrint: {
    id: "fingerspell-print",
    name: "Word/Name Printer",
    href: "/print-a-word-in-fingerspelling",
    blurb:
      "Generate a printable fingerspelling image of any name or word \u2013 handy for name labels, displays and take-home keepsakes.",
    visualKind: "preview-fingerspell-print",
  },
  signspell: {
    id: "signspell",
    name: "Today\u2019s Signspell",
    href: "/signspell",
    blurb:
      "A fresh BSL fingerspelling puzzle every day. Use the seven fingerspelling letters to find as many words as you can.",
    visualKind: "preview-signspell",
  },
  quiz: {
    id: "quiz",
    name: "Fingerspelling Quiz",
    href: "/fingerspell/quiz",
    blurb:
      "Ten fingerspelled words to read and type back. Three difficulty modes, with an optional leaderboard.",
    visualKind: "preview-quiz",
  },
  printables: {
    id: "printables",
    name: "Free BSL resources",
    href: "/free-resources",
    blurb:
      "Every free BSL tool, game and printable in one place.",
    visualKind: "chart",
    visualSrc: ALPHABET_RIGHT,
    visualAlt: "BSL fingerspelling alphabet chart",
  },
};

/* Each page picks tools from the set above and pairs them with an
   audience-specific `useNote` (one sentence of how this audience
   would actually use the tool). */

const SCHOOLS = {
  slug: "schools",
  route: "/for-schools",
  theme: "amber",
  ogImage: "/images/og/for-schools.png",
  eyebrow: "For schools",
  audienceNoun: "schools",
  title: "Free BSL resources for schools",
  dek:
    "Introduce British Sign Language with printable activities, classroom starters and interactive tools. No previous BSL knowledge is needed to use them. They provide a first introduction to signs, fingerspelling and Deaf awareness, rather than a course in BSL.",
  primaryCta: { label: "Browse classroom resources", href: "#tools" },
  secondaryCta: { label: "See the online BSL course", href: "/enrol" },
  trust: [
    "Free to use in class",
    "No sign-up or email required",
  ],
  heroVisualKind: "chart",
  heroVisualSrc: ALPHABET_RIGHT,
  heroVisualAlt: "BSL fingerspelling alphabet chart",
  featuredLessons: [
    {
      eyebrow: "Lesson plan",
      sequenceLabel: "Introductory lesson",
      title: "Say It Without Speaking",
      blurb:
        "A 45\u201360 minute introductory session exploring communication without speech, BSL fingerspelling and three everyday signs. Four classroom activities and four downloadable PowerPoint and PDF resources, no prior BSL needed.",
      meta: [
        "KS3 / KS4 (adaptable for Upper KS2)",
        "45\u201360 minutes",
        "Whole class",
        "4 free downloads",
      ],
      cta: { label: "Open the lesson plan", href: "/lesson-plan-say-it-without-speaking" },
      altCta: { label: "All lesson plans", href: "/lesson-plans" },
      /* Mirrors the actual five activities in the lesson plan so the
         deck visual tells visitors what they'll teach, not generic
         "Starter / Main / Plenary" labels. */
      deck: [
        { icon: "MicOff",      label: "Communication without speech" },
        { icon: "Image",       label: "Photos" },
        { icon: "Hand",        label: "Fingerspell" },
        { icon: "HelpCircle",  label: "Three signs" },
        { icon: "ListChecks",    label: "Recap" },
      ],
    },
    {
      eyebrow: "Lesson plan",
      sequenceLabel: "Follow-up lesson",
      title: "BSL Fingerspelling Through Play",
      blurb:
        "A 45\u201360 minute follow-up lesson in which pupils practise BSL fingerspelling through four short games \u2013 Bingo, Corners, Decode the Word and Find Your Partner. Each activity comes with its own printable pack, and several work as short classroom warm-ups on their own.",
      meta: [
        "KS2 / KS3 (adaptable for KS4)",
        "45\u201360 minutes",
        "Whole class",
        "Games pack + 3 activity packs",
      ],
      cta: { label: "Open the lesson plan", href: "/lesson-plan-fingerspelling-through-play" },
      altCta: { label: "All lesson plans", href: "/lesson-plans" },
      /* The four games, in lesson order, with the lucide icons that
         the lesson plan page itself uses (Eye / Grid3x3 / MapPin /
         HeartHandshake) for visual continuity. */
      deck: [
        { icon: "Eye",            label: "Decode" },
        { icon: "Grid3x3",        label: "Bingo" },
        { icon: "MapPin",         label: "Corners" },
        { icon: "HeartHandshake", label: "Find Partner" },
      ],
    },
  ],
  sectionHeaders: {
    tools: {
      eyebrow: "Free tools for busy classrooms",
      title: "Quick starters, printables and activities that fit into the school day",
    },
    byKeyStage: {
      eyebrow: "Ideas by age group",
      title: "Simple ways to use the resources with different ages",
    },
    calendarMoments: {
      eyebrow: "In the diary",
      title: "Dates for the school calendar",
    },
    whyTeach: {
      eyebrow: "Why it matters",
      title: "Why explore BSL at school?",
    },
    trustedResources: {
      eyebrow: "Where to go next",
      title: "Further information and specialist support",
    },
  },
  tools: [
    {
      ...TOOLS.signspell,
      useNote:
        "Put it on the board as a five-minute morning starter while the class settles or the register is taken.",
    },
    {
      ...TOOLS.fingerspell,
      useNote:
        "Pupils can watch their own names being fingerspelled, a friendly first introduction to the BSL alphabet.",
    },
    {
      ...TOOLS.fingerspellPrint,
      useNote:
        "Generate printable names and words for labels, classroom displays or pupils to take home.",
    },
    {
      ...TOOLS.vocabSheet,
      useNote:
        "Create a visual reference sheet for a current topic such as animals, weather or family.",
    },
    {
      ...TOOLS.wordSearch,
      useNote:
        "Generate a fingerspelling word search in seconds for a lesson handout.",
    },
    {
      ...TOOLS.printables,
      useNote: "Grab the BSL fingerspelling alphabet chart for the classroom wall in one click.",
    },
  ],
  byKeyStage: [
    {
      ks: "EYFS",
      years: "Reception",
      copy:
        "Introduce two or three everyday signs at a time through stories, pictures and familiar classroom routines. Greetings, family and animals are good places to start. At this age, focus on recognising and copying signs rather than learning the fingerspelling alphabet.",
      links: [
        { label: "Family signs poster", href: "/bsl-family-signs" },
        { label: "Greeting signs poster", href: "/bsl-greetings-signs" },
      ],
    },
    {
      ks: "KS1",
      years: "Years 1–2",
      copy:
        "Use simple signs alongside matching games, stories and familiar topics. Pupils who are confident with letters can begin to explore the fingerspelling alphabet and try fingerspelling their initials or short names.",
      links: [
        { label: "Family signs poster", href: "/bsl-family-signs" },
        { label: "Fingerspelling alphabet", href: "/bsl-fingerspelling-alphabet-chart" },
      ],
    },
    {
      ks: "KS2",
      years: "Years 3–6",
      copy:
        "Introduce the BSL fingerspelling alphabet and let pupils practise with their own names and familiar words. Create vocabulary sheets around current topics, or use a short game or daily puzzle as a classroom starter.",
      links: [
        { label: "Fingerspelling alphabet", href: "/bsl-fingerspelling-alphabet-chart" },
        { label: "Today's Signspell", href: "/signspell" },
      ],
    },
    {
      ks: "KS3",
      years: "Years 7–9",
      copy:
        "Use “Say It Without Speaking” to explore communication, BSL fingerspelling and a small number of introductory signs. Follow it with the fingerspelling games lesson or a themed word-search activity.",
      links: [
        { label: "Say It Without Speaking", href: "/lesson-plan-say-it-without-speaking" },
        { label: "Fingerspelling Through Play", href: "/lesson-plan-fingerspelling-through-play" },
      ],
    },
    {
      ks: "KS4",
      years: "Years 10–11",
      copy:
        "Explore BSL as a distinct language and discuss its relationship with Deaf culture and identity. Pupils can use the fingerspelling tools for short practical activities, but these resources are introductions rather than a structured BSL qualification.",
      links: [
        { label: "What is BSL?", href: "/what-is-bsl" },
        { label: "Deaf community", href: "/deaf-community" },
      ],
    },
  ],
  calendarMoments: [
    {
      when: "Around 18 March",
      title: "Sign Language Week",
      org: "British Deaf Association",
      copy:
        "Marks the anniversary of BSL being recognised as a language in its own right by the UK Government on 18 March 2003. A useful opportunity to explore Deaf history and culture, share Deaf authors and artists, and learn a new sign or two as a class. Exact dates vary each year.",
    },
    {
      when: "23 September",
      title: "International Day of Sign Languages",
      org: "United Nations",
      copy:
        "A UN day celebrating sign languages worldwide. Pair our BSL resources with a discussion of how sign languages differ between countries – BSL, American Sign Language, Irish Sign Language, Auslan and many more.",
    },
    {
      when: "Dates vary each year",
      title: "Deaf Awareness Week",
      org: "UK awareness week",
      copy:
        "A national focus on deafness and Deaf awareness. The dates and lead organiser vary from year to year, so check the current year's listing. Ideas: build a corridor display with the fingerspelling alphabet chart, run a daily Signspell challenge, or use the “Say It Without Speaking” lesson.",
    },
  ],
  whyTeach: [
    {
      title: "Practical, everyday inclusion",
      copy:
        "The National Deaf Children's Society reports 53,225 deaf children and young people in the UK (NDCS, accessed 2026). Introducing pupils to BSL can help make visual languages and Deaf identity more visible within school. Where a deaf pupil uses BSL, the school should follow the pupil's individual communication plan and seek appropriate specialist support.",
    },
    {
      title: "A full language, not gestures",
      copy:
        "BSL has its own grammar, vocabulary and history. Learning about it can broaden pupils' understanding of language and communication, with links to English, languages and Citizenship.",
    },
    {
      title: "Curriculum fit",
      copy:
        "These resources can support classroom work on communication, languages, inclusion and Deaf awareness. Teachers may find relevant links with PSHE, Citizenship and language-focused activities, depending on their curriculum and pupils.",
    },
  ],
  trustedResources: [
    {
      org: "NDCS",
      full: "National Deaf Children\u2019s Society",
      copy: "Resources for teachers supporting deaf pupils and families.",
      href: "https://www.ndcs.org.uk/",
    },
    {
      org: "BDA",
      full: "British Deaf Association",
      copy: "A Deaf-led UK charity campaigning for BSL rights, language recognition and Deaf community priorities.",
      href: "https://bda.org.uk/",
    },
    {
      org: "RNID",
      full: "Royal National Institute for Deaf People",
      copy: "Information, advice and campaigning for deaf people, people with hearing loss and tinnitus, and their families.",
      href: "https://rnid.org.uk/",
    },
    {
      org: "BATOD",
      full: "British Association of Teachers of the Deaf",
      copy: "Professional body for teachers of the deaf across the UK.",
      href: "https://www.batod.org.uk/",
    },
  ],
  softFunnel: {
    eyebrow: "Going further",
    title: "A next step for staff who want to go further",
    copy:
      "Our 10-section introductory online BSL course has been independently CPD certified. Schools can enrol several staff in one payment, and each learner receives their own course access and, on successful completion, their own certificate. It is suitable for staff who want to build on these free resources, but it is not a regulated BSL qualification.",
    cta: { label: "See the course", href: "/enrol" },
    altCta: { label: "Group enrolment", href: "/groups" },
  },
  faq: [
    {
      q: "Do I need any BSL knowledge to use these resources?",
      a:
        "No. The interactive tools are designed to be used without prior BSL knowledge. Your role is to introduce the activity and learn alongside pupils. The activities use supplied materials and are intended only as an introduction. They are not a substitute for learning with an appropriately skilled and experienced BSL teacher or tutor.",
    },
    {
      q: "Can I print these for the whole school?",
      a:
        "Yes. The free printables and tool outputs are free to use across your school. Please keep our brand mark visible, do not resell them, and do not upload them to other resource marketplaces as your own.",
    },
    {
      q: "Are these resources suitable for primary and secondary schools?",
      a:
        "Different resources suit different ages. Simple sign posters and visual activities work best with younger pupils, while older pupils can explore fingerspelling, language, Deaf culture and communication in greater depth.",
    },
    {
      q: "What about EYFS / Reception?",
      a:
        "Use a small number of signs regularly through familiar routines, stories and visual games. Pre-readers can explore simple signs before the full fingerspelling alphabet becomes meaningful.",
    },
    {
      q: "Can these resources be used with pupils with special educational needs or disabilities?",
      a: [
        "Some may be useful, depending on the pupils and the purpose of the activity. Selected tools let users slow the fingerspelling, choose a left- or right-handed view and change the displayed skin tone. Teachers should adapt activities to individual communication, sensory, motor and learning needs.",
        "BSL and Makaton are not the same. BSL is a complete natural language, while Makaton is a communication programme that uses signs, symbols and speech. These resources should not replace a pupil's established communication support or specialist advice.",
      ],
    },
    {
      // A GCSE in BSL is being developed for England (Ofqual published the
      // assessment rules in Nov 2025). We keep our free resources clearly
      // separate from it, without leaning on a status that will date quickly.
      q: "Are these resources suitable for GCSE BSL preparation?",
      a:
        "No. A GCSE in British Sign Language is being developed for England, but these resources are not aligned with its subject content or assessment requirements. They provide a general introduction to fingerspelling, selected signs and Deaf awareness, rather than a GCSE course.",
    },
    {
      q: "Can I link to these pages from my school website or VLE?",
      a:
        "Yes. Schools are welcome to link directly to the pages and tools from their website or VLE, including deep links into specific printables or the Vocab Sheet Maker.",
    },
  ],
};

const LIBRARIES = {
  slug: "libraries",
  route: "/for-libraries",
  theme: "teal",
  ogImage: "/images/og/for-libraries.png",
  eyebrow: "For libraries",
  audienceNoun: "libraries",
  title: "Free BSL resources for libraries",
  seoTitle: "Free BSL Resources for Libraries | Printables, Displays & Activities",
  seoDescription:
    "Free British Sign Language resources for libraries, including BSL printables, fingerspelling tools, storytime activities, display ideas and Deaf Awareness Week resources.",
  dek:
    "Drop-in BSL activities and printables ready for displays, storytime and Deaf Awareness Week. Staff can set up the prepared printables and introductory activities without prior BSL knowledge, and they are free for libraries to use.",
  primaryCta: { label: "Browse library tools", href: "#tools" },
  secondaryCta: { label: "See the BSL course", href: "/enrol" },
  trust: [
    "Free for library use",
    "No sign-up or email required",
    "Easy to print from desktop, tablet or mobile",
  ],
  heroVisualKind: "preview-vocab",
  tools: [
    {
      ...TOOLS.vocabSheet,
      useNote:
        "Build a themed A4 sheet (animals, colours, weather) for the children\u2019s corner table.",
    },
    {
      ...TOOLS.fingerspell,
      useNote:
        "Fingerspell a child\u2019s name or a book character\u2019s name on screen during storytime.",
    },
    {
      ...TOOLS.fingerspellPrint,
      useNote:
        "Print a storytime character\u2019s name, a visiting author\u2019s name or a welcome-desk nameplate in BSL fingerspelling.",
    },
    {
      ...TOOLS.wordSearch,
      useNote:
        "Generate a fingerspelling word search for a half-term activity sheet.",
    },
    {
      ...TOOLS.signspell,
      useNote:
        "A quiet daily BSL fingerspelling challenge for older children and teens.",
    },
    {
      ...TOOLS.printables,
      useNote:
        "Print a BSL fingerspelling alphabet chart as a simple visual reference for staff and visitors.",
    },
  ],
  sectionHeaders: {
    tools: {
      eyebrow: "Free tools for library spaces",
      title: "Quick printables and activities for displays, desks and drop-in sessions",
    },
    byKeyStage: {
      eyebrow: "By visitor group",
      title: "What works for each visitor type",
    },
    topicPacks: {
      eyebrow: "Display & activity packs",
      title: "Printable picks for your library",
    },
    staffGuides: {
      eyebrow: "Communication & access",
      title: "Welcome desk and event guidance for your staff",
    },
    whyTeach: {
      eyebrow: "Worth offering",
      title: "Why offer BSL resources at your library?",
    },
  },
  byKeyStage: [
    {
      ks: "Pre-school",
      years: "Storytime & toddler groups",
      copy:
        "Pin the family signs poster behind your storytime corner. Show a small number of signs together at the start of each session and repeat them at later sessions.",
      links: [
        { label: "Family signs poster", href: "/bsl-family-signs" },
        { label: "Greeting signs poster", href: "/bsl-greetings-signs" },
      ],
    },
    {
      ks: "Primary-age",
      years: "After-school & half-term",
      copy:
        "Build custom Vocab Sheets for your activity table around your current display theme (animals, weather, colours). Stack themed word search sheets nearby for take-home.",
      links: [
        { label: "Vocab Sheet Maker", href: "/bsl-vocabulary-sheet-creator" },
        { label: "Word Search Maker", href: "/fingerspelling-word-search-maker" },
      ],
    },
    {
      ks: "Teens & young people",
      years: "Drop-in challenges",
      copy:
        "Open the Fingerspelling Quiz on a tablet in the teen area or at an activity table – ten fingerspelled words and an instant score. Today\u2019s Signspell makes a low-stakes daily challenge.",
      links: [
        { label: "Fingerspelling Quiz", href: "/fingerspell/quiz" },
        { label: "Today\u2019s Signspell", href: "/signspell" },
      ],
    },
    {
      ks: "Family & adult visitors",
      years: "Lifelong learners",
      copy:
        "Display the fingerspelling alphabet at the welcome desk. Adults who want to go further can take our 10-section CPD-certified online course.",
      links: [
        { label: "All free resources", href: "/free-resources" },
        { label: "BSL course", href: "/enrol" },
      ],
    },
  ],
  topicPacks: [
    {
      label: "Welcome desk alphabet",
      sub: "Right-handed & left-handed",
      href: "/bsl-fingerspelling-alphabet-chart",
    },
    {
      label: "Family signs",
      sub: "Mum, dad, brother, sister – pre-loaded",
      href: "/bsl-vocabulary-sheet-creator?topic=family",
    },
    {
      label: "Greeting signs",
      sub: "Welcome poster set",
      href: "/bsl-greetings-signs",
    },
    {
      label: "Animals",
      sub: "For children\u2019s corner – pre-loaded",
      href: "/bsl-vocabulary-sheet-creator?topic=animals",
    },
    {
      label: "Colours",
      sub: "Bright wall display – pre-loaded",
      href: "/bsl-vocabulary-sheet-creator?topic=colours",
    },
    {
      label: "Name fingerspelling",
      sub: "For storytime characters",
      href: "/fingerspell",
    },
  ],
  staffGuides: [
    {
      title: "Communicating with Deaf visitors",
      intro:
        "Quick reminders for the welcome desk, drop-in sessions and event spaces.",
      items: [
        "Ask the person how they prefer to communicate.",
        "Get their attention before speaking.",
        "Face them and keep your mouth visible.",
        "Use good lighting and avoid standing with a bright window behind you.",
        "Speak normally rather than shouting or exaggerating your mouth movements.",
        "Do not assume everyone can lip-read.",
        "Offer written information, but do not assume writing works for every BSL user.",
      ],
      printable: { kind: "poster", label: "Download welcome desk poster" },
    },
    {
      title: "Planning an accessible event",
      intro:
        "For author talks, storytime sessions and Deaf Awareness Week events.",
      items: [
        "Ask attendees about communication or access requirements when they book.",
        "Arrange appropriate communication support, including a registered BSL/English interpreter where requested.",
        "Book communication support as early as possible.",
        "Check that hearing loops or other assistive listening systems are working.",
        "Use captioned videos and provide important information in writing.",
        "Make sure the room is well lit and attendees have a clear view of speakers and interpreters.",
      ],
      link: {
        label: "Find a registered communication professional through NRCPD",
        href: "https://www.nrcpd.org.uk/find-a-communication-professional",
        external: true,
      },
      printable: { kind: "checklist", label: "Download event checklist" },
    },
    {
      title: "A welcoming display is a good start",
      copy:
        "BSL printables can help make visual language more visible, but accessible library services may also include working hearing loops, captioned content, email or text contact options, clear written information, Deaf awareness training and a straightforward process for arranging communication support.",
    },
    {
      title: "Add your library\u2019s access information",
      intro:
        "Use these questions to create a short local access guide for staff.",
      items: [
        "What assistive listening systems, including hearing loops, are available, and where can visitors use them?",
        "How can visitors contact the library without making a telephone call?",
        "What is the process for arranging a BSL/English interpreter?",
        "How can visitors request access support for an event?",
        "Where can staff find the library service\u2019s accessibility policy?",
      ],
      printable: { kind: "fillable", label: "Download fill-in sheet" },
    },
  ],
  calendarMoments: [
    {
      when: "Dates vary each year",
      title: "Deaf Awareness Week",
      org: "UK Council on Deafness",
      copy:
        "A useful annual opportunity for Deaf awareness activities in libraries. Run a wall display with the alphabet chart, stock activity sheets at the children\u2019s table all week, and use one of the printable sign sheets to introduce a few simple signs during storytime.",
    },
    {
      when: "Dates vary each year",
      title: "Sign Language Week",
      org: "British Deaf Association",
      copy:
        "An annual celebration of BSL\u2019s recognition as a language. Curate a Deaf history book display, stack alphabet bookmarks at the issue desk, and share a sign of the day on social media.",
    },
    {
      when: "23 September",
      title: "International Day of Sign Languages",
      org: "United Nations",
      copy:
        "A UN-recognised day celebrating sign languages worldwide. Add a short note to your display explaining that British Sign Language is different from American Sign Language, Irish Sign Language, Auslan and other sign languages around the world.",
    },
  ],
  whyTeach: [
    {
      title: "Practical, everyday inclusion",
      copy:
        "The National Deaf Children\u2019s Society reports there are more than 50,000 deaf children and young people in the UK (NDCS, accessed 2026). Many more adults live with deafness or hearing loss, so visible, respectful resources can help make your library feel more welcoming.",
    },
    {
      title: "Programming without expertise",
      copy:
        "The activities are designed to be self-explanatory. Staff do not need fluent BSL to print a sheet, set up a display or open a tool on a tablet.",
    },
    {
      title: "Beyond books",
      copy:
        "Libraries already offer more than books. BSL resources sit naturally alongside the inclusion, accessibility and lifelong-learning materials your service already provides.",
    },
  ],
  trustedResources: [
    {
      org: "NDCS",
      full: "National Deaf Children\u2019s Society",
      copy: "Resources for families with deaf children and young people.",
      href: "https://www.ndcs.org.uk/",
    },
    {
      org: "BDA",
      full: "British Deaf Association",
      copy: "A Deaf-led UK charity campaigning for BSL rights, language recognition and Deaf community priorities.",
      href: "https://bda.org.uk/",
    },
    {
      org: "RNID",
      full: "Royal National Institute for Deaf People",
      copy: "Information, advice and campaigning for deaf people, people with hearing loss and tinnitus, and their families.",
      href: "https://rnid.org.uk/",
    },
  ],
  downloads: [
    {
      label: "BSL alphabet poster (right-handed PDF)",
      href: ALPHABET_PDF_R,
      external: true,
    },
    {
      label: "BSL alphabet poster (left-handed PDF)",
      href: ALPHABET_PDF_L,
      external: true,
    },
    {
      label: "Family signs poster",
      href: "/bsl-family-signs",
    },
    {
      label: "Greeting signs poster",
      href: "/bsl-greetings-signs",
    },
  ],
  softFunnel: {
    eyebrow: "Going further",
    title: "Visitors interested in learning more?",
    copy:
      "Share our 10-section CPD-certified online BSL course with visitors who want to go further. Learners work through it in their own time, on any device.",
    cta: { label: "See the BSL course", href: "/enrol" },
    altCta: { label: "Browse all free resources", href: "/free-resources" },
  },
  faq: [
    {
      q: "Can we use these in paid library events?",
      a:
        "Yes. The resources are free to use in public library events, including ticketed events. Please keep our brand mark visible and do not resell, repackage or upload the resources as your own.",
    },
    {
      q: "Are the activities suitable for children?",
      a:
        "Yes. The alphabet chart, family signs, name fingerspelling activities and word searches can all work well for primary-age children. Younger children should use the interactive tools with adult support.",
    },
    {
      q: "Can we share these with other branches?",
      a:
        "Yes. Point colleagues at this page or the relevant tool URL. Everything is free to use across your service.",
    },
    {
      q: "What if a Deaf visitor needs communication support?",
      a:
        "These resources are for introductory learning, displays and simple visual reference. They are not a substitute for appropriate communication support or a registered BSL/English interpreter. For events, appointments, detailed enquiries or sensitive conversations, follow your library service\u2019s accessibility procedures and arrange the appropriate registered communication support, such as a BSL/English interpreter, where needed.",
    },
    {
      q: "Where do the signs in the Vocab Sheet Maker come from?",
      a:
        "From our public BSL sign library at british-sign.co.uk. Each sign has a name and an illustration; you choose which ones go on the sheet. BSL signs can vary by region and community, so visitors may see some signs used differently elsewhere.",
    },
    {
      q: "Can we link to these pages from our library website or catalogue?",
      a:
        "Yes, please do. The page URLs are stable and we maintain them. Deep links into specific printables or the Vocab Sheet Maker work the same way.",
    },
    {
      q: "Will you keep adding new signs and resources?",
      a:
        "Yes. The sign library grows over time as we publish new vocabulary on the main site. The Vocab Sheet Maker draws from the latest library each time.",
    },
  ],
};

const TUTORS = {
  slug: "tutors",
  route: "/for-tutors",
  theme: "purple",
  ogImage: "/images/og/for-tutors.png",
  eyebrow: "For BSL tutors",
  audienceNoun: "BSL tutors",
  title: "Free teaching tools for BSL tutors",
  dek:
    "Build lesson-specific vocab sheets, fingerspelling worksheets and word searches for your learners in minutes. These resources are designed to support BSL tutors, not replace live teaching from a qualified tutor.",
  primaryCta: {
    label: "Open the Vocab Sheet Maker",
    href: "/bsl-vocabulary-sheet-creator",
  },
  // Keep the hero resource-led: the course gets its own lower funnel
  // section, so the second hero action points at more free tools
  // rather than competing with the resources at the top.
  secondaryCta: { label: "Browse all free resources", href: "/free-resources" },
  trust: [
    "No sign-up required",
    "Each generated resource includes a small british-sign.co.uk credit",
    "New signs are added to the resource library over time",
  ],
  heroVisualKind: "preview-vocab",
  sectionHeaders: {
    tools: {
      eyebrow: "Free tools for BSL teaching",
      title: "Quick printables, warmers and homework tasks for your lessons",
    },
    byKeyStage: {
      eyebrow: "By learner level",
      title: "Tools that work for each stage",
    },
    topicPacks: {
      eyebrow: "Topic packs",
      title: "Quick topic picks for lesson handouts",
    },
    whyTeach: {
      eyebrow: "Worth knowing",
      title: "How these resources fit into your teaching",
    },
  },
  tools: [
    {
      ...TOOLS.vocabSheet,
      useNote:
        "Build a topic-specific A4 vocab sheet for each lesson, using your chosen colour palette.",
    },
    {
      ...TOOLS.wordSearch,
      useNote:
        "Generate a word search using this week\u2019s lesson vocabulary as a quick warmer or homework task.",
    },
    {
      ...TOOLS.fingerspell,
      useNote:
        "Show new learners how their name is fingerspelled - an easy, engaging way in.",
    },
    {
      ...TOOLS.fingerspellPrint,
      useNote:
        "Print a name badge sheet for new learners, or a glossary card of key fingerspelled terms for your current topic.",
    },
    {
      ...TOOLS.quiz,
      useNote:
        "Set the quiz as low-stakes homework and compare scores at the start of next session.",
    },
    {
      ...TOOLS.signspell,
      useNote:
        "Open today\u2019s Signspell at the start of class as a 5-minute warmer.",
    },
  ],
  useCases: [
    {
      title: "Topic-tailored vocab sheets",
      copy:
        "Search the sign library by name, add your 6\u201312 chosen signs to a sheet, pick a colour and download. Repeat for each topic or module.",
    },
    {
      title: "Quick lesson warmer",
      copy:
        "Open Today\u2019s Signspell at the start of class. Five minutes of useful practice, with no setup.",
    },
    {
      title: "Check what learners remember",
      copy:
        "Set the Fingerspelling Quiz as homework between lessons. Learners get an instant score, and you get a quick sense of where the group is.",
    },
    {
      title: "Names for new learners",
      copy:
        "Use the Fingerspelling Player to show each learner their own name fingerspelled. A friendly way to get started in week one.",
    },
  ],
  byKeyStage: [
    {
      ks: "Absolute beginners",
      years: "Getting started",
      copy:
        "Learners\u2019 own names are a friendly way in: use the Fingerspelling Player to show each name, then the printer for a take-home name card. A greetings vocab sheet and a few everyday signs make an easy first handout.",
      links: [
        { label: "Fingerspelling Player", href: "/fingerspell" },
        { label: "Greeting signs poster", href: "/bsl-greetings-signs" },
      ],
    },
    {
      ks: "Improvers",
      years: "Growing vocabulary",
      copy:
        "Build topic-specific vocab sheets for each module (family, colours, weather, food, work). Drop a themed word search into homework so learners revisit each sign visually between sessions.",
      links: [
        { label: "Vocab Sheet Maker", href: "/bsl-vocabulary-sheet-creator" },
        { label: "Word Search Maker", href: "/fingerspelling-word-search-maker" },
      ],
    },
    {
      ks: "Intermediate",
      years: "Building fluency",
      copy:
        "Use the Fingerspelling Quiz as quick, no-pressure practice between sessions. Today\u2019s Signspell makes a good five-minute warm-up to keep fingerspelling fresh while you focus on grammar and conversation in class.",
      links: [
        { label: "Fingerspelling Quiz", href: "/fingerspell/quiz" },
        { label: "Today\u2019s Signspell", href: "/signspell" },
      ],
    },
    {
      ks: "Family & community classes",
      years: "Mixed groups",
      copy:
        "For mixed-ability community classes, lean on the Vocab Sheet Maker and printables. They give every learner the same visual reference to take home, which is useful for parent-and-child sessions, community groups and Saturday drop-ins.",
      links: [
        { label: "Family signs poster", href: "/bsl-family-signs" },
        { label: "Vocab Sheet Maker", href: "/bsl-vocabulary-sheet-creator" },
      ],
    },
  ],
  topicPacks: [
    {
      label: "BSL fingerspelling alphabet",
      sub: "Right-handed & left-handed",
      href: "/bsl-fingerspelling-alphabet-chart",
    },
    {
      label: "Family signs",
      sub: "Mum, dad, brother, sister \u2013 pre-loaded",
      href: "/bsl-vocabulary-sheet-creator?topic=family",
    },
    {
      label: "Greeting signs",
      sub: "Hello, please, thank you",
      href: "/bsl-greetings-signs",
    },
    {
      label: "Colours",
      sub: "Common colour vocabulary \u2013 pre-loaded",
      href: "/bsl-vocabulary-sheet-creator?topic=colours",
    },
    {
      label: "Animals",
      sub: "For early modules \u2013 pre-loaded",
      href: "/bsl-vocabulary-sheet-creator?topic=animals",
    },
    {
      label: "Weather",
      sub: "For seasonal lessons \u2013 pre-loaded",
      href: "/bsl-vocabulary-sheet-creator?topic=weather",
    },
  ],
  calendarMoments: [
    {
      when: "Dates vary each year",
      title: "Deaf Awareness Week",
      org: "UK Council on Deafness",
      copy:
        "A major UK moment for Deaf awareness. Plan a themed week of lessons \u2013 open with a Deaf history discussion, build a wall display from the alphabet chart, and finish with learners fingerspelling a chosen word as a group activity.",
    },
    {
      when: "Dates vary each year",
      title: "Sign Language Week",
      org: "British Deaf Association",
      copy:
        "Marks the anniversary of the UK Government recognising BSL as a language in its own right (18 March 2003). A natural time to revisit Deaf-led content, introduce learners to Deaf authors, creators and organisations, and share a sign of the day on social media.",
    },
    {
      when: "23 September",
      title: "International Day of Sign Languages",
      org: "United Nations",
      copy:
        "A UN-recognised day celebrating sign languages worldwide. A useful chance to show learners that British Sign Language is one of many sign languages \u2013 American Sign Language, Irish Sign Language and Auslan are all different.",
    },
  ],
  whyTeach: [
    {
      title: "Support materials, not a replacement for a tutor",
      copy:
        "These tools are designed to sit alongside the work of a qualified BSL tutor. They are quick to set up, but they don\u2019t teach grammar, classifiers, role shift, fluency, feedback or the lived experience a Deaf tutor brings to a lesson. Use them as warmers, homework and visual handouts, not as the lesson itself.",
    },
    {
      title: "Consistent look across your modules",
      copy:
        "Every sheet you generate uses the same sign library, illustration style and A4 layout. Learners build a coherent set of handouts over a term, even if they print them themselves.",
    },
    {
      title: "Tablet-friendly",
      copy:
        "The interactive tools work on a phone, tablet or computer. Useful if you teach in venues where you can\u2019t rely on a projector, or if you set tasks for learners to do between lessons.",
    },
    {
      title: "Free, with stable URLs",
      copy:
        "Every tool is free and lives at a stable URL on british-sign.co.uk. Safe to link from a course handbook, a VLE or your own teaching website.",
    },
  ],
  trustedResources: [
    {
      org: "BDA",
      full: "British Deaf Association",
      copy: "A Deaf-led UK charity campaigning for BSL rights, language recognition and Deaf community priorities.",
      href: "https://bda.org.uk/",
    },
    {
      org: "NDCS",
      full: "National Deaf Children\u2019s Society",
      copy: "Resources for families with deaf children and young people.",
      href: "https://www.ndcs.org.uk/",
    },
    {
      org: "RNID",
      full: "Royal National Institute for Deaf People",
      copy: "Information, advice and campaigning for deaf people, people with hearing loss and tinnitus, and their families.",
      href: "https://rnid.org.uk/",
    },
    {
      org: "SignHealth",
      full: "SignHealth",
      copy: "Deaf-led health charity providing services and information in BSL.",
      href: "https://signhealth.org.uk/",
    },
  ],
  downloads: [
    {
      label: "BSL alphabet poster (right-handed PDF)",
      href: ALPHABET_PDF_R,
      external: true,
    },
    {
      label: "BSL alphabet poster (left-handed PDF)",
      href: ALPHABET_PDF_L,
      external: true,
    },
    {
      label: "Family signs poster",
      href: "/bsl-family-signs",
    },
    {
      label: "Greeting signs poster",
      href: "/bsl-greetings-signs",
    },
  ],
  softFunnel: {
    eyebrow: "A next step for learners",
    title: "Share our 10-section online BSL course with learners",
    copy:
      "When learners want more practice between sessions, our 10-section CPD-certified online BSL course is a useful next step. They work through it in their own time, on any device.",
    cta: { label: "Share the course", href: "/enrol" },
    altCta: { label: "All free resources", href: "/free-resources" },
  },
  faq: [
    {
      q: "Can I use these resources in paid classes?",
      a:
        "You can use the tools and printables in your own teaching, including paid classes. Please keep the british-sign.co.uk brand mark visible and do not resell, repackage or upload the resources as your own.",
    },
    {
      q: "Can I edit the printable sheets?",
      a:
        "The generated sheets are designed to be printed as they are. You can choose the signs, title and colour before downloading, but please keep the british-sign.co.uk brand mark visible.",
    },
    {
      q: "Will you add more signs to the library?",
      a:
        "Yes, the sign library grows over time as we publish new vocabulary on the main site. Revisit the Vocab Sheet Maker periodically and the latest signs will be there.",
    },
    {
      q: "Can I use these instead of my own teaching materials?",
      a:
        "No. They are designed as support materials for warmers, handouts and homework. They do not replace your own lesson planning, live teaching or feedback.",
    },
    {
      q: "Are the signs regional?",
      a:
        "BSL signs can vary by region, community and signer. Our library aims to show commonly used signs, but learners may see different variants elsewhere. This can be a useful teaching point rather than a problem.",
    },
    {
      q: "Can I link these from my own course materials?",
      a:
        "Yes. You can link directly to the tools from your course handbook, VLE or teaching website. Please link to the live pages rather than copying the resources elsewhere, so learners always get the latest version.",
    },
  ],
};

const SCOUTS = {
  slug: "scouts",
  route: "/for-scouts-guides",
  theme: "forest",
  ogImage: "/images/og/for-scouts-guides.png",
  eyebrow: "For Scouts, Guides & youth groups",
  audienceNoun: "groups",
  title: "Free BSL resources for Scouts, Guides & youth groups",
  seoTitle: "Free BSL Resources for Scouts, Guides & Youth Groups",
  seoDescription:
    "Free British Sign Language activities and printables for Scouts, Cubs, Beavers, Squirrels, Brownies, Guides, Rainbows and Rangers. Useful alongside the Scouts Communicator Activity Badge and the Girlguiding Communicate skills builder.",
  dek:
    "Drop-in BSL activities and printables for Scouts, Guides and other youth groups. Designed for short, focused sessions that leaders can run without prior BSL knowledge.",
  primaryCta: { label: "Browse group tools", href: "#tools" },
  secondaryCta: { label: "See the BSL course", href: "/enrol" },
  trust: [
    "Free to use with your group",
    "No sign-up or email required",
    "Easy to print from desktop, tablet or mobile",
  ],
  heroVisualKind: "preview-fingerspell",
  featuredLesson: {
    eyebrow: "Meeting plan",
    title: "Say It Without Speaking",
    blurb:
      "A 45\u201360 minute introductory session on non-spoken communication, BSL fingerspelling and a few beginner British Sign Language signs. Four activities and four downloadable PowerPoint and PDF resources \u2013 no prior BSL knowledge needed. Adaptable for Cubs, Scouts, Brownies, Guides and similar sections.",
    meta: [
      "Cubs / Scouts / Guides (adaptable younger & older)",
      "45\u201360 minutes",
      "Whole section, unit or group",
      "4 free downloads",
    ],
    cta: { label: "Open the meeting plan", href: "/lesson-plan-say-it-without-speaking" },
    altCta: { label: "All lesson plans", href: "/lesson-plans" },
  },
  sectionHeaders: {
    tools: {
      eyebrow: "Free tools for meetings & camps",
      title: "Quick activities, printables and challenges for a single meeting",
    },
    physicalGames: {
      eyebrow: "Practical BSL games",
      title: "Active, quiet and team-based activities for meeting nights and camps",
    },
    byKeyStage: {
      eyebrow: "By section",
      title: "What works for each age group",
    },
    topicPacks: {
      eyebrow: "Activity ideas",
      title: "Themed picks for badge work and meeting nights",
    },
    whyTeach: {
      eyebrow: "Why include BSL",
      title: "Why include BSL in your programme",
    },
  },
  tools: [
    {
      ...TOOLS.fingerspell,
      useNote:
        "Members watch their own name fingerspelled in BSL \u2013 a simple, memorable first step.",
    },
    {
      ...TOOLS.fingerspellPrint,
      useNote:
        "Print each member\u2019s name in BSL fingerspelling for a name-badge night or section noticeboard.",
    },
    {
      ...TOOLS.vocabSheet,
      useNote:
        "Build a themed sheet around an evening\u2019s topic (woodland animals, weather, family) and hand out as a takeaway.",
    },
    {
      ...TOOLS.wordSearch,
      useNote:
        "Generate a fingerspelling word search around your section\u2019s programme theme in seconds.",
    },
    {
      ...TOOLS.signspell,
      useNote:
        "A short BSL fingerspelling puzzle that works as a calm 5-minute activity for older sections.",
    },
    {
      ...TOOLS.printables,
      useNote:
        "Print the BSL fingerspelling alphabet chart to pin up in your meeting place or take to camp.",
    },
  ],
  useCases: [
    {
      title: "One-meeting BSL taster",
      copy:
        "Open the Fingerspelling Player on a screen, let members fingerspell their name, then hand out a printed name strip and the alphabet chart to take home. Around 20 minutes, with only a few minutes of preparation.",
    },
    {
      title: "Communicator badge work",
      copy:
        "Use the tools to support communication and BSL-themed badge work across youth groups. They fit well alongside the Scouts Communicator Activity Badge (Cubs, Scouts and similar sections) and Girlguiding\u2019s \u201CSign it spell it\u201D activity within the Communicate Stage 3 skills builder (Brownies, Guides) \u2013 the Fingerspelling Player and alphabet chart match the introducing-yourself and short-word recognition tasks particularly well. Badge requirements can change, so check the current official badge or skills-builder page before running the activity. Use content created by Deaf BSL tutors or Deaf-led organisations. For a live session, invite a Deaf BSL tutor or another suitably qualified BSL tutor, agree the activity in advance and offer appropriate payment.",
    },
    {
      title: "Camp evening activity",
      copy:
        "Build a themed vocab sheet (woodland animals, weather, family) before camp and print enough for the group. Use it in a well-lit hut, marquee or sheltered activity space for a 15\u201320 minute activity. Fingerspelling needs clear lighting and visibility.",
    },
    {
      title: "Inclusion week",
      copy:
        "Combine a printed BSL alphabet chart, a themed fingerspelling word search and a short discussion about Deaf culture as part of an inclusion-themed programme block.",
    },
  ],
  physicalGamesSafetyNote:
    "Leaders should adapt movement-based games to their venue, group and usual risk assessment. Walking, wheeling and tabletop versions are equally valid \u2013 score them the same as running versions.",
  physicalGamesDifficultyNote:
    "Print your fingerspelling letter cards with the written letter beneath the image for beginners, or with the image alone for a harder challenge. The same card sets work across most of the games below.",
  gamesPackUrl: "/print/games-pack",
  physicalGames: [
    {
      title: "Fingerspelling Decoder Relay",
      featured: true,
      packUrl: "/print/games-pack/relay",
      practises: "Recognising fingerspelling letters and reading complete words",
      time: "10\u201315 min",
      age: "Ages 8+ (Cubs / Brownies and older)",
      setting: "Active",
      size: "Teams of 3\u20136",
      equipment: "One A\u2013Z set of fingerspelling letter cards per team, plus duplicate copies of common letters. Use cards with the written letter beneath the image for beginners, or image-only cards with the answer on the back for a harder version. A written target word per team.",
      preparation: "Lay each team\u2019s card set out at the far end of the room, fingerspelling side up. Decide whether your word list uses common letter duplicates or avoids repeats altogether.",
      howToPlay: "Give each team a written word. One member at a time travels to the cards, finds the fingerspelling letter they think matches the first written letter, and returns with it. If using image-only cards, they check the answer on the back. With beginner cards, the written letter is already shown beneath the image. The next member fetches the next letter. Players may check only one card per visit. If it is wrong, they return it fingerspelling-side up and go back to their team. Once the team has the full word, they arrange the cards and one member fingerspells the complete word for another team to read.",
      harder: "Use image-only letter cards. Show the target word once with the Fingerspelling Player, then hide it before the relay begins.",
      inclusive: "Players walk, wheel, or nominate a card finder. The activity should not depend on running speed.",
    },
    {
      title: "Fingerspelling Trail",
      practises: "Producing fingerspelling letters in sequence",
      time: "10 min",
      age: "Ages 6+ (Beavers / Rainbows and older)",
      setting: "Active",
      size: "Any size",
      equipment: "Fingerspelling letter cards (image-only for a harder challenge, or with the written letter shown beneath for beginners), plus hoops, spots or marked positions to mark each step.",
      preparation: "Lay the cards beside (not on) hoops or spots arranged in a trail across the room or field. A seated version uses cards on a table with players moving a counter along the route.",
      howToPlay: "Show the word using the Fingerspelling Player, or fingerspell it live if you are confident doing so. Players move through the trail by touching, pointing to or standing beside the correct letters in the correct order, and must form each fingerspelling letter themselves before moving to the next position.",
      harder: "No written word displayed \u2013 use the Fingerspelling Player to spell the target word twice (or do it live) and the group must decode it before starting.",
      inclusive: "Run the seated table version with a counter, or use a printed grid that players tick through with a pen.",
    },
    {
      title: "Decode the Kit",
      featured: true,
      packUrl: "/print/games-pack/kit",
      practises: "Decoding complete fingerspelled words and matching them to objects",
      time: "15\u201320 min",
      age: "Ages 8+ (Cubs / Brownies and older)",
      setting: "Themed \u00b7 well-lit hall, hut or marquee",
      size: "Teams of 3\u20136",
      equipment: "Real camp items (torch, tent peg, rope, cup, boot, map, billy can). Word cards showing each item\u2019s name entirely in BSL fingerspelling images, with no printed English beneath. A leader answer sheet.",
      preparation: "Lay the camp items out on a table or around the well-lit space. Shuffle the fingerspelled word cards and place them face-down. Run this in a hut, marquee or sheltered activity space \u2013 fingerspelling needs clear visibility.",
      howToPlay: "Teams take a card, decode the fingerspelled word, find the matching item and bring both to the leader. Once the team has correctly decoded the word and found the item, the leader gives them their next card.",
      harder: "Show the fingerspelled word for ten seconds then hide it, so teams have to remember it before they search. Or after collecting the object, one member fingerspells its name to another team.",
      inclusive: "Bring items to the team rather than the team to the items, or run as a tabletop version using item picture cards instead of physical kit.",
    },
    {
      title: "Human Word Builder",
      practises: "Recognising fingerspelling letters and putting them into word order",
      time: "10\u201315 min",
      age: "Ages 6+ (Beavers / Rainbows and older)",
      setting: "Calm \u00b7 indoor",
      size: "Whole section",
      equipment: "One large fingerspelling letter card per member (with the written letter beneath for beginners, or image-only for a harder challenge), plus a leader\u2019s prepared list of target words that can actually be built from the cards distributed.",
      preparation: "Hand each member a single letter card. Make sure your word list only uses letters that are present in the group \u2013 avoid repeated letters unless duplicate cards have been issued.",
      howToPlay: "Show the word using the Fingerspelling Player, or fingerspell it live if you are confident doing so. The members holding the required letters step forward and arrange themselves in the correct order.",
      harder: "Do not show the written word \u2013 the Fingerspelling Player (or a confident leader) fingerspells it twice and the group decodes it.",
      inclusive: "Members can hold cards while seated and form a line or semicircle. Allow a quieter pace so no one is rushed.",
    },
    {
      title: "Fingerspelled Team Calls",
      practises: "Reading fingerspelling for a real purpose, repeatedly across a session",
      time: "Whole meeting / camp",
      age: "Any age with adaptation (start with 3-letter names for younger sections)",
      setting: "Background activity",
      size: "Any size",
      equipment: "A small printed card per team showing their team name. The BSL fingerspelling alphabet poster on the wall.",
      preparation: "Pick team names that match your programme \u2013 e.g. FOX, OWL, DEER, ROBIN. Start with three-letter names for younger sections. Display the alphabet chart nearby. Use the Fingerspelling Player to show each team name slowly twice, or fingerspell it live if you are confident doing so.",
      howToPlay: "For the rest of the session, summon teams by showing their name with the Fingerspelling Player, or fingerspell it live if you are confident doing so. Award a small point each time a team responds, and an extra point for any team that correctly recognises another team\u2019s call.",
      harder: "Use longer names (BADGER, KESTREL) once basics are confident. Add an unfamiliar bonus word as a one-off call.",
      inclusive: "Repeat each name as many times as needed. Accurate reading matters more than responding first \u2013 don\u2019t frame speed as the only success measure.",
    },
    {
      title: "Decode the Trail",
      featured: true,
      packUrl: "/print/games-pack/trail",
      practises: "Reading complete fingerspelled words",
      time: "20\u201330 min",
      age: "Ages 8+ (Cubs / Brownies and older)",
      setting: "Outdoor / large venue",
      size: "Teams of 3\u20136",
      equipment: "Numbered station markers. At each station, a card showing a short word entirely in BSL fingerspelling images (e.g. TENT, GATE, TREE, HALL, ROPE, MAP). One leader answer sheet.",
      preparation: "Place a fingerspelled word card at each of the numbered stations around your venue. Give each team the same set of stations but a different starting point so groups don\u2019t all converge.",
      howToPlay: "Teams visit each numbered station in order from their starting point. At each station, they decode the fingerspelled word and record it on their answer sheet before moving to the next station. They finish when they have decoded every word.",
      harder: "Add a short instruction after each decoded word for teams to complete before moving on.",
      inclusive: "Run as a tabletop version: numbered envelopes on tables instead of stations across a field.",
    },
    {
      title: "Fingerspelling Bingo",
      featured: true,
      packUrl: "/print/games-pack/bingo",
      practises: "Reading fingerspelled words",
      time: "10\u201315 min",
      age: "Ages 8+ (Cubs / Brownies and older)",
      setting: "Calm \u00b7 indoor",
      size: "Any size",
      equipment: "A bingo grid per player containing short written words or names. A leader call list at three difficulty levels (3-letter words, 4\u20135-letter words, names and camp vocabulary).",
      preparation: "Print enough bingo grids for each player, plus your leader call list.",
      howToPlay: "Show each word using the Fingerspelling Player on a screen, or fingerspell it live if you are confident doing so \u2013 slowly and twice. Players cross off the word they think they saw. First full row wins; first full sheet wins extra points.",
      harder: "Move up to names and longer programme vocabulary. Show each word only once.",
      inclusive: "Allow players to ask for a repeat, and keep a paper copy of the alphabet chart on the table for anyone who needs a reference.",
    },
    {
      title: "Find Your Partner",
      practises: "Matching written letters to fingerspelling letters",
      time: "10 min",
      age: "Ages 6+ (Beavers / Rainbows and older)",
      setting: "Calm \u00b7 indoor",
      size: "Even numbers, any size",
      equipment: "Half the group gets written-letter cards; the other half gets matching fingerspelling cards.",
      preparation: "Shuffle and distribute one card per person, face down. Each member checks their own card without revealing it to others.",
      howToPlay: "Members may not say the letter aloud. Anyone holding a written letter forms that letter using BSL fingerspelling; anyone holding a fingerspelling image looks for the matching written-letter holder. Pairs meet and confirm by showing one another their cards.",
      harder: "Use short written words paired with the same words shown entirely in fingerspelling images.",
      inclusive: "Seated version: place cards on tables and pair them up rather than walking around. Use larger print for anyone with low vision.",
    },
    {
      title: "Decode the Word",
      featured: true,
      practises: "Reading fingerspelled words \u2013 great as a recurring meeting opener",
      time: "5 min",
      age: "Any age with adaptation (start with 3-letter words)",
      setting: "Calm \u00b7 indoor",
      size: "Whole section",
      equipment: "Mini whiteboards and pens, paper, or a small bag of letter tiles per team.",
      preparation: "Pick three target words at the right level for the group.",
      howToPlay: "Show the word using the Fingerspelling Player on a screen, or fingerspell it live if you are confident doing so \u2013 slowly and twice. Teams write the word on their mini whiteboard, or build it from letter tiles. Reveal answers together. Progress from three-letter words up to programme vocabulary over a term.",
      harder: "Show each word only once. Move to four\u2013five-letter words and names.",
      inclusive: "Replay the word more slowly, pause between letters, or let a confident member repeat the complete word afterwards for any team that needs it.",
    },
    {
      title: "Fingerspelling Corners",
      featured: true,
      packUrl: "/print/games-pack/corners",
      practises: "Fast letter recognition",
      time: "5\u201310 min",
      age: "Ages 6+ (Beavers / Rainbows and older)",
      setting: "Active",
      size: "Any size",
      equipment: "Four large fingerspelling letter cards (one per corner \u2013 image-only for a harder challenge, or with the written letter beneath for beginners). A leader call list of single letters.",
      preparation: "Place the four cards in the corners of the room. Plan to swap them out every few rounds.",
      howToPlay: "The leader shows or calls a written letter. Members move to the corner whose fingerspelling image matches. Reveal the answer together, then continue with the next round. Keep everyone involved rather than eliminating players. Change the four corner letters periodically.",
      harder: "Call out or display a written letter. Members form the fingerspelling letter themselves before moving to the matching image-only corner.",
      inclusive: "Members point at the matching corner, hold up a number card, or move a counter on a printed table map. Run a seated version where corners are areas of a tablecloth.",
    },
    {
      title: "Fingerspelling Snap & Pairs",
      featured: true,
      packUrl: "/print/games-pack/snap",
      practises: "Matching written letters to fingerspelling letters",
      time: "10 min",
      age: "Ages 6+ (Beavers / Rainbows and older)",
      setting: "Calm \u00b7 indoor",
      size: "Pairs or small groups",
      equipment: "A matching card set containing each written letter once and its corresponding fingerspelling image once.",
      preparation: "For Pairs, lay all cards face-down in a grid. For Snap, shuffle and deal evenly between players.",
      howToPlay: "Pairs: players take turns turning over two cards and keep the pair if the written letter matches its fingerspelling image. Most pairs wins. Snap: players take turns placing cards face-up into the centre. Call \u201CSnap\u201D when the top two cards form a matching pair: a written letter and its corresponding fingerspelling image.",
      harder: "Use whole-word cards instead of letters (written word paired with the same word in fingerspelling images).",
      inclusive: "Pairs is the quieter, slower-paced option \u2013 better for younger groups, calm-downs and sensory-aware sessions.",
    },
    {
      title: "Who Am I?",
      practises: "Reading names and other people\u2019s fingerspelling",
      time: "10 min",
      age: "Ages 8+ (Cubs / Brownies and older)",
      setting: "Calm \u00b7 indoor",
      size: "Whole section",
      equipment: "A printed list of group members\u2019 first names for the leader. Optional: alphabet chart on the wall.",
      preparation: "Brief the group beforehand and let members volunteer. Never put anyone on the spot. Only use members\u2019 names with their agreement \u2013 fictional characters, animals and team names work just as well.",
      howToPlay: "Members stand or sit in a circle. One volunteer fingerspells the first name of someone in the group. The rest decode the name and write it on a mini whiteboard or piece of paper before revealing their answers together. Expand to leader names, fictional characters, animals or team names as confidence grows.",
      harder: "Longer names, two-word character names or less familiar fictional characters.",
      inclusive: "Allow the volunteer to fingerspell alongside a partner, or use the Fingerspelling Player on a screen rather than producing live.",
    },
    {
      title: "Fingerspelling Message Chain",
      practises: "Careful observation and accurate production",
      time: "10 min",
      age: "Ages 8+ (Cubs / Brownies and older)",
      setting: "Calm or active \u00b7 indoor",
      size: "Teams of 4\u20138",
      equipment: "A list of short target words per chain (start with three-letter words).",
      preparation: "Teams stand or sit in lines, with everyone facing away from the person behind them until tapped.",
      howToPlay: "Show the final person in each line a short written word. They tap the next person and fingerspell it once. The word passes along the line. The first person in the line writes (or selects) the word they received. Compare with the original and discuss why clear hand position, pace and eye contact matter.",
      harder: "Use longer words, names, or do not allow any repeats along the chain.",
      inclusive: "Allow a second look at each letter if needed. Score for accuracy rather than ranking teams on speed.",
    },
    {
      title: "Word Stations",
      practises: "Reading complete fingerspelled words and matching them to pictures",
      time: "15 min",
      age: "Ages 8+ (Cubs / Brownies and older)",
      setting: "Active \u00b7 indoor or outdoor",
      size: "Teams of 2\u20134",
      equipment: "Word cards showing short words entirely in BSL fingerspelling images, placed around the hall. A team answer sheet. A central table displaying the numbered objects or pictures (animals, camp equipment, weather, food, colours or section names).",
      preparation: "Place each fingerspelled word card at a numbered station around the room. Display the matching objects or pictures all together at a central table \u2013 not beside the words \u2013 so the answer isn\u2019t obvious from each station.",
      howToPlay: "Teams visit each station, decode the fingerspelled word, then visit the central table and record the number of the matching object or picture on their answer sheet.",
      harder: "Hide a short written instruction inside each station. Teams must decode the word AND follow the instruction.",
      inclusive: "Run a tabletop version with the cards spread across tables, or bring each station card to the teams.",
    },
  ],
  byKeyStage: [
    {
      ks: "Squirrels & Rainbows",
      years: "Ages 4\u20136",
      copy:
        "Stick to a few visual, repeatable signs (hello, please, thank you, family signs, a couple of animals). Use the Family Signs and Greeting Signs posters together and reuse the same handful of signs across a few meetings so they actually stick.",
      links: [
        { label: "Family signs poster", href: "/bsl-family-signs" },
        { label: "Greeting signs poster", href: "/bsl-greetings-signs" },
      ],
    },
    {
      ks: "Beavers & Brownies",
      years: "Ages 6\u20139",
      copy:
        "Introduce the BSL fingerspelling alphabet visually using the printable chart. Members use the Fingerspelling Player to learn how to fingerspell their own first name. Build a vocab sheet around your current programme theme to take home. Focus on introducing themselves, exchanging names and recognising short fingerspelled words.",
      links: [
        { label: "Fingerspelling alphabet (PDF)", href: ALPHABET_PDF_R, external: true },
        { label: "Fingerspelling Player", href: "/fingerspell" },
      ],
    },
    {
      ks: "Cubs & Guides",
      years: "Ages 8\u201314",
      copy:
        "These activities work well alongside communication-themed badge activities. Practise introducing yourself, exchange names with a partner, and recognise short fingerspelled words from the alphabet poster. Today\u2019s Signspell can be a short 5-minute opener at the start of a meeting.",
      links: [
        { label: "Word Search Maker", href: "/fingerspelling-word-search-maker" },
        { label: "Today\u2019s Signspell", href: "/signspell" },
      ],
    },
    {
      ks: "Scouts, Explorers & Rangers",
      years: "Ages 10\u201318",
      copy:
        "Older members can practise introductions, exchange names, recognise longer fingerspelled words and learn a short BSL conversation using established signs \u2013 with fingerspelling used for names and unfamiliar words rather than for an entire conversation. The Vocab Sheet Maker and Fingerspelling Quiz both support this kind of practice. Where possible, invite a Deaf BSL tutor, Deaf-led organisation or qualified BSL tutor to contribute, with the activity and access agreed in advance and suitable payment offered for their time. If communication support is needed during the visit, arrange a registered BSL/English interpreter separately.",
      links: [
        { label: "Vocab Sheet Maker", href: "/bsl-vocabulary-sheet-creator" },
        { label: "Fingerspelling Quiz", href: "/fingerspell/quiz" },
      ],
    },
  ],
  topicPacks: [
    {
      label: "BSL fingerspelling alphabet",
      sub: "Right-handed & left-handed posters",
      href: "/bsl-fingerspelling-alphabet-chart",
    },
    {
      label: "Family signs",
      sub: "Mum, dad, brother, sister and more",
      href: "/bsl-vocabulary-sheet-creator?topic=family",
    },
    {
      label: "Greeting signs",
      sub: "Hello, goodbye, please, thank you",
      href: "/bsl-greetings-signs",
    },
    {
      label: "Animals",
      sub: "Great for camps and outdoor evenings",
      href: "/bsl-vocabulary-sheet-creator?topic=animals",
    },
    {
      label: "Weather",
      sub: "Sun, rain, snow, wind \u2013 pre-loaded",
      href: "/bsl-vocabulary-sheet-creator?topic=weather",
    },
    {
      label: "Colours",
      sub: "Red, blue, green, yellow \u2013 pre-loaded",
      href: "/bsl-vocabulary-sheet-creator?topic=colours",
    },
  ],
  whyTeach: [
    {
      title: "Fits values you already work to",
      copy:
        "Inclusion, communication and respect sit at the heart of both Scouting and Guiding. Introducing BSL is a concrete way to make those values visible at your meeting place.",
    },
    {
      title: "A full language, not just gestures",
      copy:
        "BSL has its own grammar, vocabulary and history. It was formally recognised as a language in its own right by the UK Government in 2003, and the British Sign Language Act 2022 gave it legal recognition as a language of England, Wales and Scotland (the Act does not extend to Northern Ireland). Even an introductory taster helps members understand language and Deaf identity more clearly.",
    },
    {
      title: "Useful for badge programmes",
      copy:
        "The Scouts Communicator Activity Badge and the Girlguiding Communicate skills builder both include sign language as a recognised method. These free tools give leaders a quick starting point for those tasks.",
    },
    {
      title: "Works for one-off and ongoing",
      copy:
        "You don\u2019t need to commit to weeks of programming. The tools work for a single 20-minute meeting block, a camp evening, an inclusion week, or as small recurring openers across a term.",
    },
  ],
  calendarMoments: [
    {
      when: "Dates vary each year",
      title: "Deaf Awareness Week",
      org: "Annual UK awareness week",
      copy:
        "A natural moment to weave a short BSL activity into your programme. Open with the alphabet chart, fingerspell members\u2019 names and finish with a short word search to take home.",
    },
    {
      when: "Dates vary each year",
      title: "Sign Language Week",
      org: "British Deaf Association",
      copy:
        "Marks the UK Government\u2019s recognition of BSL in March 2003 (now reinforced by the BSL Act 2022). A good week for a programme block on language, identity and accessibility.",
    },
    {
      when: "23 September",
      title: "International Day of Sign Languages",
      org: "United Nations",
      copy:
        "A UN-recognised day celebrating sign languages worldwide. Pair our BSL tools with a short discussion of how sign languages differ between countries (BSL, American Sign Language, Irish Sign Language, Auslan and many more).",
    },
  ],
  trustedResources: [
    {
      org: "Scouts UK",
      full: "The Communicator Activity Badge",
      copy: "Official badge page for the Scouts Communicator Activity Badge \u2013 includes sign language tasks members can complete in BSL.",
      href: "https://www.scouts.org.uk/scouts/activity-badges/communicator/",
    },
    {
      org: "Girlguiding",
      full: "Communicate skills builder \u2013 Stage 3",
      copy: "Official Girlguiding page for the Communicate Stage 3 skills builder, including the BSL-based \u201CSign it spell it\u201D activity.",
      href: "https://www.girlguiding.org.uk/what-we-do/our-badges-and-activities/badge-finder/communicate-stage-3/",
    },
    {
      org: "BDA",
      full: "British Deaf Association",
      copy: "A Deaf-led UK charity campaigning for BSL rights, language recognition and Deaf community priorities.",
      href: "https://bda.org.uk/",
    },
    {
      org: "NDCS",
      full: "National Deaf Children\u2019s Society",
      copy: "Resources for adults working with deaf children, including youth and activity leaders.",
      href: "https://www.ndcs.org.uk/",
    },
  ],
  downloads: [
    {
      label: "BSL alphabet poster (right-handed PDF)",
      href: ALPHABET_PDF_R,
      external: true,
    },
    {
      label: "BSL alphabet poster (left-handed PDF)",
      href: ALPHABET_PDF_L,
      external: true,
    },
    {
      label: "Family signs poster",
      href: "/bsl-family-signs",
    },
    {
      label: "Greeting signs poster",
      href: "/bsl-greetings-signs",
    },
  ],
  softFunnel: {
    eyebrow: "Going further",
    title: "A next step for leaders who want to go further",
    copy:
      "Our 10-section CPD-certified online BSL course suits leaders who want to take it further after these free resources. Pay What You Can from \u00a35 per learner. Group enrolment is a single payment, and each learner gets their own certificate.",
    cta: { label: "See the course", href: "/enrol" },
    altCta: { label: "Group enrolment", href: "/groups" },
  },
  faq: [
    {
      q: "Do I need any BSL knowledge to use these resources?",
      a:
        "No. The tools are designed to be used without prior BSL knowledge. Your role is to set up the activity and learn alongside your group. They use the supplied materials for introductory activities and are not a substitute for teaching from an appropriately skilled and experienced BSL tutor.",
    },
    {
      q: "Can these be used as part of the Communicator Activity Badge?",
      a:
        "These tools can support preparation for the BSL-based tasks, but do not complete the current badge requirements on their own. The current sign-language option includes holding a conversation, describing an experience and helping facilitate a short conversation between a sign-language user and someone who does not sign. Always check the official Scouts page, as the badge is being refreshed.",
    },
    {
      q: "Do they work for Brownies and Guides too?",
      a:
        "Yes. The Girlguiding Communicate Stage 3 skills builder includes a BSL activity (\u201CSign it spell it\u201D). The Fingerspelling Player, Word/Name Printer and alphabet chart work particularly well with the activity. Always check the official Girlguiding page for the current requirements.",
    },
    {
      q: "Are they suitable for younger sections (Beavers, Squirrels, Rainbows)?",
      a:
        "Yes, with the right pick. Stick to the visual printables (family signs, greeting signs) and use a few signs repeatedly over several meetings. Save the full fingerspelling alphabet for sections where members are confident with letters.",
    },
    {
      q: "Can I print these for the whole group?",
      a:
        "Yes. The free printables and tool outputs are free to use with your group. Please keep our brand mark visible, do not resell them, and do not upload them to other resource marketplaces as your own.",
    },
    {
      q: "What if a member of our group is Deaf or hard of hearing?",
      a:
        "Talk directly with the member in an age-appropriate way, involving their family or carers where appropriate. Ask what communication and access arrangements work for them rather than making assumptions. If inviting a Deaf contributor, agree the purpose and format in advance, offer appropriate payment, and do not expect one person to represent the whole Deaf community.",
    },
    {
      q: "Can I link to this page from our group\u2019s website or newsletter?",
      a:
        "Yes, please do. The pages and tools are stable URLs we maintain. Deep links into specific printables or the Vocab Sheet Maker work the same way.",
    },
  ],
};


export const AUDIENCE_PAGES = {
  schools: SCHOOLS,
  libraries: LIBRARIES,
  tutors: TUTORS,
  scouts: SCOUTS,
};

export default AUDIENCE_PAGES;
